import { RepeatOneSharp, Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, Button, CircularProgress, Container, CssBaseline, Grid, IconButton, TextField, Typography } from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";
import { isAxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { json, Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { gateway } from "../../gateway";
import { setAppState } from "../../redux/features/appStateSlice";
import { updateSignupBusinessState } from "../../redux/features/signupBusinessStateSlice";
import { updateSignupEmployeeState } from "../../redux/features/signupEmployeeStateSlice";
import { googleOauthLogin } from "../../utils";
import { SnackbarAlert } from "../common/SnackbarAlert";
import { ShieldIcon } from "../ShieldIcon";
import { TwoFaModal } from "./TwoFaModal";
import { colors } from "../ui/colors";
import { inputStyles } from "../ui/styles/inputStyles";
import { useSnackbar } from "../common/snackbar/useSnackbar";
import { GoogleButtonAuthenticate } from "../common/google/GoogleButtonAuthenticate";

export const SignIn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isVisible, setIsVisible] = useState(false);
  const [emailInUse, setEmailInUse] = useState(false);
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const [is2FAVisible, setIs2FAVisible] = useState(false);
  const [is2FADisabled, setIs2FADisabled] = useState(false);
  const [userGoogleFedered, setUserGoogleFedered] = useState(null);
  const [credentials, setCredentials] = useState({});
  const [user, setUser] = useState(null);
  const emailFromParams = searchParams.get("email") || "";
  const [formData, setFormData] = useState({ email: emailFromParams, password: "" });

  const dispatch = useDispatch();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const handleSignInGoogle = (data) => {
    setUserGoogleFedered(data);
  };

  const pathSignup = "/signup";
  const pathForgot = "forgotpassword";
  const currPath = "/signin";
  const home = "/";

  useEffect(() => {
    const timeout = setTimeout(() => setIsVisible(true), 200);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (userGoogleFedered) {
      googleOauthLogin(userGoogleFedered?.credential, userGoogleFedered?.clientId, userGoogleFedered?.select_by)
        .then((res) => res.data)
        .then((data) => {
          if (data?.status === "pending" && data?.nextSteps !== null) {
            dispatch(updateSignupEmployeeState({ ...data?.userInfo, isOauth: true, currentView: 2 }));
            dispatch(updateSignupBusinessState({ ...data?.userInfo, isOauth: true }));
            navigate(`${pathSignup}#classify`);
          }
          if (data?.token?.length > 0 && data?.refresh_token?.token?.length > 0) {
            const { token, refresh_token } = data;

            // Store tokens first
            sessionStorage.setItem("token", token);
            sessionStorage.setItem("refresh_token", refresh_token.token);

            showSnackbar("Signin successfully. Redirecting...", "success");

            // Use href for clean reload with tokens
            setTimeout(() => {
              window.location.href = home;
            }, 100);
          }
        })
        .catch((error) => {
          if (isAxiosError(error) && error.response) {
            const { data } = error.response;
            setSnackbarMessage(data.message);
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGoogleFedered]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const data = new FormData(event.currentTarget);
    const userData = {
      email: data.get("email").trim().toLocaleLowerCase(),
      password: data.get("password"),
    };

    if (!userData.email || !userData.password) {
      showSnackbar("Email and password cannot be empty", "error");
      setIsLoading(false);
      return;
    }

    setCredentials(userData);

    try {
      const response = await gateway.post("/users/signin-send-twofa", userData, { withCredentials: true });

      // Nuevo device
      if (response.status === 202 && response.data.message === "Send Two Factor Code") {
        setIs2FAVisible(true);
        setIsLoading(false);
        const user = response?.data?.data?.user;
        setUser(user);
      }

      // Cuenta no verificada
      if (response.data.message === "Verify Account") {
        const payload = {
          activationToken: response.data.data.activationToken,
          uid: response.data.data.user.uid,
        };

        localStorage.setItem("pendingActivationAccount", JSON.stringify(payload));
        const role = response?.data?.data?.user?.role;


        if (role === "business" || role === "other") {
          navigate("/signup/business");
        } else {
          navigate("/signup/individual");
        }

        setIsLoading(false);
      }

      // Login exitoso sin 2FA
      if (response.status === 200 && response.data.message === "Successfully authenticated without 2FA") {
        const token = response.data.data.token;
        const refresh_token = response.data.data.refresh_token.token;

        // Store tokens first
        sessionStorage.setItem("token", token);
        sessionStorage.setItem("refresh_token", refresh_token);

        showSnackbar("Signin successfully. Redirecting...", "success");

        // Small delay to ensure storage is written before reload
        setTimeout(() => {
          window.location.href = home; // Use href instead of navigate + reload
        }, 100);

        setIsLoading(false);
      }
    } catch (error) {
      if (error.response?.status === 401) {
        showSnackbar("Incorrect credentials.", "error");
      } else {
        showSnackbar("An error occurred. Please try later", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (emailArg, code) => {
    setIsLoading(true);
    try {
      const response = await gateway.post("/users/signin-verify-twofa", { email: emailArg, code }, { withCredentials: true });

      if (response.status === 200) {
        const { token, refresh_token } = response.data.data;

        // Store tokens first
        sessionStorage.setItem("token", token);
        sessionStorage.setItem("refresh_token", refresh_token.token);

        showSnackbar("Code verified successfully!", "success");

        setIs2FADisabled(true);

        // Use href for clean reload with tokens
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
      } else {
        setError("Invalid 2FA Code");
        showSnackbar("Invalid 2FA Code", "error");
      }
    } catch (error) {
      setError("An error occurred while verifying the 2FA code.");
      showSnackbar("An error occurred while verifying the 2FA code.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToSignIn = () => {
    setIs2FAVisible(false);
    setEmailInUse(false);
    setError("");
    setToken("");
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Container component="main" maxWidth="sm">
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          opacity: isVisible ? 1 : 0,
          transition: "opacity 0.5s",
          position: "relative",
        }}
      >
        <Box
          sx={{
            backgroundColor: "var(--fx-surface-base, #FFFFFF)",
            boxShadow: "0 8px 12px -2px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
            p: 3.5,
            borderRadius: "16px",
            height: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            filter: is2FAVisible ? "blur(1px)" : "none",
          }}
        >
          <ShieldIcon />
          <Typography
            component="h1"
            variant="h1"
            sx={{
              fontWeight: 1000,
              color: colors.gray.base,
              mb: 1,
              fontSize: "48px",
              letterSpacing: "1px",
            }}
          >
            SIGN IN
          </Typography>

          <SignInForm onSubmit={handleSubmit} emailInUse={emailInUse} isLoading={isLoading} onChange={handleChange} initialEmail={emailFromParams} />

          <Grid container spacing={2} sx={{ mt: 2, mb: 3 }}>
            <Grid item xs={6}>
              <RouterLink
                onClick={() => {
                  dispatch(setAppState({ prevPathState: currPath, currPathState: pathSignup }));
                }}
                to={pathForgot}
                style={{ color: colors.info.base, textDecoration: "underline", fontSize: "14px", fontWeight: 500 }}
              >
                Forgot Password?
              </RouterLink>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "right" }}>
              <RouterLink
                onClick={() => {
                  dispatch(setAppState({ prevPathState: currPath, currPathState: pathSignup }));
                }}
                to={pathSignup}
                style={{ color: colors.info.base, textDecoration: "underline", fontSize: "14px", fontWeight: 500 }}
              >
                Don't have an account? Sign Up
              </RouterLink>
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
            <GoogleButtonAuthenticate redirectSignUp="/signup/business" handleSignInGoogle={handleSignInGoogle} />
          </Box>
        </Box>

        <TwoFaModal
          open={is2FAVisible}
          onClose={handleBackToSignIn}
          email={credentials.email}
          password={credentials.password}
          user={user}
          userPhone={null}
          onVerify={handleVerifyCode}
          isVerifying={isLoading}
          isDisabled={is2FADisabled}
        />
      </Box>

      <SnackbarAlert scope="global" openState={snackbar.open} setOpenState={closeSnackbar} severity={snackbar.severity} message={snackbar.message} />
    </Container>
  );
};

const SignInForm = ({ onSubmit, emailInUse, isLoading, initialEmail = "" }) => {
  const [showPassword, setShowPassword] = useState(false);
  const passwordInputRef = useRef(null);

  useEffect(() => {
    if (initialEmail && passwordInputRef.current) {
      // Pequeño delay para asegurar que el campo esté renderizado
      setTimeout(() => {
        passwordInputRef.current?.focus();
      }, 100);
    }
  }, [initialEmail]);

  return (
    <Box component="form" onSubmit={onSubmit} noValidate sx={{ mt: 3, width: "100%" }}>
      <TextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="Email Address"
        error={emailInUse}
        name="email"
        autoComplete="email"
        defaultValue={initialEmail}
        autoFocus={!initialEmail}
        sx={inputStyles}
      />
      <TextField
        margin="normal"
        required
        fullWidth
        name="password"
        label="Password"
        type={showPassword ? "text" : "password"}
        error={emailInUse}
        helperText={emailInUse ? "Bad Credentials" : ""}
        id="password"
        autoComplete="current-password"
        inputRef={passwordInputRef}
        sx={{ ...inputStyles, mb: 2 }}
        InputProps={{
          endAdornment: (
            <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)} sx={{ color: colors.gray[400] }}>
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          ),
        }}
      />
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isLoading}
        sx={{
          py: 1.5,
          borderRadius: "12px",
          backgroundColor: colors.inverse.base,
          color: colors.inverse.text,
          fontSize: "16px",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          boxShadow: "none",
          "&:hover": { backgroundColor: colors.inverse.hover, boxShadow: "none" },
          "&:disabled": { backgroundColor: colors.gray[200], color: colors.gray[400] },
        }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : "SIGN IN"}
      </Button>
    </Box>
  );
};
